import { booleanAttribute, ChangeDetectorRef, computed, DestroyRef, Directive, inject, input, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, NgControl, ValidationErrors } from "@angular/forms";

export type LabelVariant = "over" | "on" | "in";

const defaultErrorMessage = (key: string, errors: ValidationErrors): string => {
    const messages: Record<string, string> = {
        required: "This field is required",
        email: "Invalid email address",
        maxlength: `Maximum length is ${errors.maxlength?.requiredLength}`,
        minlength: `Minimum length is ${errors.minlength?.requiredLength}`
    };

    return messages[key] ?? "Invalid value";
};

/**
 * Shared ControlValueAccessor + validation plumbing for the kno2-form-* wrappers.
 *
 * Self-injects its NgControl (instead of registering NG_VALUE_ACCESSOR) so the wrapper can
 * read its own control state, and derives invalid/error state reactively from control.events.
 * Subclasses provide the value representation (writeValue + handleChange) and the template.
 */
@Directive()
export abstract class FormControlBaseDirective implements ControlValueAccessor, OnInit {
    protected readonly ngControl = inject(NgControl, { self: true, optional: true });
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);

    public readonly invalid = input<boolean | undefined>(undefined);
    public readonly errors = input<ValidationErrors>();
    public readonly showErrorOn = input<"touched" | "dirty" | "never">("touched");
    public readonly label = input<string>();
    public readonly labelVariant = input<LabelVariant>("on");
    public readonly info = input<string>();
    public readonly infoTitle = input<string>("Tip");
    public readonly required = input<boolean, unknown>(false, { transform: booleanAttribute });

    protected readonly disabled = signal(false);
    protected readonly fullWidthStyle = { width: "100%" };
    protected readonly standaloneNgModel = { standalone: true };
    private readonly statusVersion = signal(0);

    protected readonly showError = computed(() => {
        this.statusVersion();
        const control = this.ngControl?.control;
        if (!control || control.valid) return false;
        if (this.showErrorOn() === "never") return false;
        if (this.showErrorOn() === "dirty") return control.dirty;
        return control.touched;
    });

    protected readonly showInvalid = computed(() => this.invalid() ?? this.showError());

    protected readonly errorMessage = computed(() => {
        this.statusVersion();
        const controlErrors = this.ngControl?.control?.errors;
        if (!controlErrors) return null;
        const key = Object.keys(controlErrors)[0];
        return this.errors()?.[key] ?? defaultErrorMessage(key, controlErrors);
    });

    protected onChange: (value: unknown) => void = () => {};
    protected onTouched: () => void = () => {};

    protected emitChange(value: unknown): void {
        this.onChange(value);
        this.onTouched();
    }

    constructor() {
        if (this.ngControl) this.ngControl.valueAccessor = this;
    }

    public ngOnInit(): void {
        this.ngControl?.control?.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.statusVersion.update((version) => version + 1));
    }

    public writeValue(value: unknown): void {
        this.applyValue(value);
        this.cdr.markForCheck();
    }

    protected abstract applyValue(value: unknown): void;

    public registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
        this.cdr.markForCheck();
    }

    protected markTouched(): void {
        this.onTouched();
    }
}
